import { GridRowSelectionModel } from "@mui/x-data-grid";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { utils, writeFile } from "xlsx-js-style";

import {
  deleteListOfRequest,
  getListOfRequest,
  getListRequestStatus,
  getListRequestType,
} from "../../api/ListOfRequest";
import { IDropDownList } from "../../interfaces/IDropDownList";
import { IFormLoR } from "../../interfaces/IFormLoR";
import { ITrRequest } from "../../interfaces/ITrRequest";
import { selectUserProfile, selectUserRole } from "../../store/auth/selector";

interface Return {
  data: {
    requestStatus: any[];
    requestType: any[];
    filter: any;
    loadingActive: boolean;
    listTrRequest: ITrRequest[];
  };
  method: {
    onGetRequestStatus(): void;
    onGetRequestType(): void;
    onDeleteSelected(selectedRows: GridRowSelectionModel): void;
    onSubmitForm(values: any): void;
    onGetListOfRequest(search?: string): void;
    handleExport(): void;
  };
}

interface FormFilter {
  startDateValue: string;
  endDateValue: string;
  requestTypeValue: string;
  requestStatusValue: string;
}

const INITIAL_FIRST_DATA: IDropDownList = { value: "All", label: "All" };

export default function useViewModel(): Return {
  const [loadingActive, setLoadingActive] = useState(false);
  const [requestType, setRequestType] = useState<IDropDownList[]>([]);
  const [requestStatus, setRequestStatus] = useState<IDropDownList[]>([]);
  const [listTrRequest, setListTrRequest] = useState<ITrRequest[]>([]);
  const [filter, setFilter] = useState<FormFilter>({
    endDateValue: "",
    requestStatusValue: "",
    requestTypeValue: "",
    startDateValue: "",
  });

  const profile = useSelector(selectUserProfile);
  const profileRole = useSelector(selectUserRole);

  const onGetRequestType = useCallback(async () => {
    setLoadingActive(true);
    await getListRequestType()
      .then((res) => {
        res.unshift(INITIAL_FIRST_DATA);
        setRequestType(res);
      })
      .then(() => {
        setLoadingActive(false);
      })
      .catch((error) => {
        console.log("Error fetching data:", error);
        setLoadingActive(false);
      });
  }, [getListRequestType]);

  const onGetRequestStatus = useCallback(async () => {
    setLoadingActive(true);
    await getListRequestStatus(profileRole.roleId)
      .then((res) => {
        res.unshift(INITIAL_FIRST_DATA);
        setRequestStatus(res);
      })
      .then(() => {
        setLoadingActive(false);
      })
      .catch((error) => {
        console.log("Error fetching data:", error);
        setLoadingActive(false);
      });
  }, [getListRequestStatus]);

  const onGetListOfRequest = useCallback(
    async (search?: string) => {
      setLoadingActive(true);
      await getListOfRequest(
        filter.requestTypeValue,
        filter.requestStatusValue,
        filter.startDateValue,
        filter.endDateValue,
        profileRole.roleId,
        profile.BinusianId,
        search
      )
        .then((res) => setListTrRequest(res))
        .then(() => {
          setLoadingActive(false);
        })
        .catch((error) => {
          console.log("Error fetching data:", error);
          setLoadingActive(false);
        });
    },
    [getListOfRequest, filter]
  );

  const onDeleteSelected = useCallback(
    async (selectedRows: GridRowSelectionModel) => {
      await deleteListOfRequest(selectedRows as string[], profile.BinusianId)
        .then(() => onGetListOfRequest())
        .catch((error) => console.log("Error fetching data:", error));
    },
    [deleteListOfRequest, filter]
  );

  const onSubmitForm = useCallback((values: IFormLoR) => {
    setFilter({
      startDateValue: values.startDate,
      endDateValue: values.endDate,
      requestTypeValue: values.requestType,
      requestStatusValue: values.status,
    });
  }, []);

  const handleExport = () => {
    const formatDate = (inputDate) => {
      const date = new Date(inputDate);
      const day = date.getDate();
      const month = date.toLocaleString("default", { month: "long" });
      const year = date.getFullYear();

      if (day == 1 && month == "January" && year == 1970) {
        return "-";
      }
      return `${day} ${month} ${year}`;
    };

    const formattedList: any = (listTrRequest || []).map((item) => {
      const {
        requestID, // Exclude requestID dalam variable sendiri
        requestStatusID,
        requestTypeID,
        noSubstitution,
        department,
        buLeaderBinusianID,
        signerBinusianID,
        title,
        detailArea,
        area,
        binusianID,
        needSK,
        ...rest // ambil object lain ke temporary 'rest' object
      } = item;

      // Create a cell style with wrapText set to true
      const cellStyleWithLineBreakWrapText = {
        alignment: { wrapText: true },
      };

      const noPOA = `${item.noPOA}\n${noSubstitution}`;
      const businessUnit =
        item.businessUnit != department
          ? `${item.businessUnit} - ${department}`
          : item.businessUnit;
      return {
        ...rest,
        noPOA: { v: noPOA, t: "s", s: cellStyleWithLineBreakWrapText },
        titleAgreement: {
          v: item.titleAgreement,
          t: "s",
          s: cellStyleWithLineBreakWrapText,
        },
        counterpart: {
          v: item.counterpart,
          t: "s",
          s: cellStyleWithLineBreakWrapText,
        },
        businessUnit: {
          v: businessUnit,
          t: "s",
          s: cellStyleWithLineBreakWrapText,
        },
        requestDate: formatDate(item.requestDate),
        poaDate: formatDate(item.poaDate),
        startMasterUsePeriod: formatDate(item.startMasterUsePeriod),
        endMasterUsePeriod: formatDate(item.endMasterUsePeriod),
      };
    });

    const worksheet = utils.json_to_sheet(formattedList, {
      cellDates: true,
    });

    // Urutan Nama Header
    const headers = [
      "Request Status",
      "Request No.",
      "Request Date",
      "Name",
      "Business Unit",
      "CRR No.",
      "Counterpart",
      "Title of Agreement",
      "Request Type",
      "Document Type",
      "POA Date",
      "POA No.",
      "Signer",
      "Delegatee",
      "Substitutor",
      "Start Master Use Period",
      "End Master Use Period",
    ];

    // Add header row to the worksheet
    utils.sheet_add_aoa(worksheet, [headers], { origin: "A1" });

    // Get the header range
    const headerRange = utils.decode_range(worksheet["!ref"]!);
    headerRange.s.r = 0; // Set the start row to 0 for the header row

    // Apply styling to the header row
    // eslint-disable-next-line no-plusplus
    for (let C = headerRange.s.c; C <= headerRange.e.c; C++) {
      const cellAddress = utils.encode_cell({ r: 0, c: C }); // Address like A1, B1, C1, ...
      worksheet[cellAddress].s = {
        font: { bold: true },
        alignment: { vertical: "center", horizontal: "center" },
      };
    }

    const columnWidths = [
      { wch: 25 }, // Lebar kolom untuk "Request Status",
      { wch: 25 }, // Lebar kolom untuk "Request Number",
      { wch: 25 }, // Lebar kolom untuk "Request Date",
      { wch: 35 }, // Lebar kolom untuk "Binusian Name",
      { wch: 35 }, // Lebar kolom untuk "Business Unit",
      { wch: 25 }, // Lebar kolom untuk "No. CRR",
      { wch: 35 }, // Lebar kolom untuk "Counterpart",
      { wch: 35 }, // Lebar kolom untuk "Title of Agreement",
      { wch: 25 }, // Lebar kolom untuk "Request Type",
      { wch: 25 }, // Lebar kolom untuk "Document Type",
      { wch: 25 }, // Lebar kolom untuk "PoA Date",
      { wch: 35 }, // Lebar kolom untuk "No. PoA",
      { wch: 40 }, // Lebar kolom untuk "Signer",
      { wch: 40 }, // Lebar kolom untuk "Delegatee",
      { wch: 40 }, // Lebar kolom untuk "Substitutor",
      { wch: 25 }, // Lebar kolom untuk "Start Master User Period",
      { wch: 25 }, // Lebar kolom untuk "End Master User Period",
    ];

    columnWidths.forEach((col, index) => {
      worksheet["!cols"] = worksheet["!cols"] || [];
      worksheet["!cols"][index] = col;
    });

    const workbook = utils.book_new();
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();

    utils.book_append_sheet(workbook, worksheet, "Sheet1");
    writeFile(workbook, `ListOfRequestReport_${mm}${dd}${yyyy}.xlsx`);
  };

  return {
    data: {
      requestStatus,
      requestType,
      filter,
      loadingActive,
      listTrRequest,
    },
    method: {
      onGetRequestStatus,
      onGetRequestType,
      onDeleteSelected,
      onSubmitForm,
      onGetListOfRequest,
      handleExport,
    },
  };
}
